import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';
import ManageBenefitFeaturesHeader from './components/header';
import { FeatureCategory, type Feature } from './components/item';
import ManageBenefitFeaturesList from './components/list';

export interface ManageBenefitFeaturesProps {
  features: Feature[];
  onGenerateWithAi?: () => void;
  isLoadingGenerateWithAi?: boolean;
  onChange: (features: Feature[]) => void;
  showRankField?: boolean;
}

// Important: `defaultValues` are required in translations as this component is used in columbia.

const ManageBenefitFeatures = ({
  features,
  onGenerateWithAi,
  isLoadingGenerateWithAi,
  onChange,
  showRankField,
}: ManageBenefitFeaturesProps) => {
  const { t } = useTranslation();

  const onAddFeature = () => {
    const rank = features.length + 1;
    onChange([
      ...features,
      {
        title: t('manageBenefitFeatures.newFeature.title', {
          defaultValue: 'New feature',
        }),
        description: t('manageBenefitFeatures.newFeature.description', {
          defaultValue: 'New feature description',
        }),
        icon: 'cash-line',
        category: FeatureCategory.Lifestyle,
        rank: showRankField ? rank : undefined,
      },
    ]);
  };

  const onRemoveFeature = (index: number) => {
    onChange(features.filter((_, index_) => index_ !== index));
  };

  const onChangeFeature = (changeIndex: number, changeFeature: Feature) => {
    onChange(
      features.map((feature, index) =>
        index === changeIndex ? changeFeature : feature
      )
    );
  };

  return (
    <Wrapper>
      <ManageBenefitFeaturesHeader
        onGenerateWithAi={onGenerateWithAi}
        isLoadingGenerateWithAi={isLoadingGenerateWithAi}
        onAddManually={onAddFeature}
      />
      <ManageBenefitFeaturesList
        features={features}
        onChangeFeature={onChangeFeature}
        onRemoveFeature={onRemoveFeature}
        showRankField={showRankField}
      />
    </Wrapper>
  );
};

export default memo(ManageBenefitFeatures);

const Wrapper = styled.div`
  ${({ theme }) => css`
    padding: ${theme.spacing(2)};
    width: 100%;
  `}
`;
